import { Boundaries } from "@planzone/core";
import { calendarHolidays } from "@planzone/dto";
import { events } from "@planzone/events";
import { createStateReducer } from "../../utils/createStateReducer";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { State, Selector } from "./types";
import { createSelector } from "./selector";

const initialState: State = [];

function handleRetrieveDefaultCalendarHolidaysRequest(
  state: State,
  planzone: Boundaries["retrieveDefaultCalendarHolidays"]["request"],
): State {
  const index = state.findIndex((req) => req.planzone.uuid === planzone.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], request: setRequestStateToPending() },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          planzone,
          request: setRequestStateToPending(),
          defaultCalendarHolidays: { ...calendarHolidays, uuid: "" },
        },
      ];
}

function handleRetrieveDefaultCalendarHolidaysSuccess(
  state: State,
  {
    planzone,
    defaultCalendarHolidays,
  }: Boundaries["retrieveDefaultCalendarHolidays"]["success"],
): State {
  const index = state.findIndex((req) => req.planzone.uuid === planzone.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          ...state[index],
          request: setRequestStateToSuccess(),
          defaultCalendarHolidays,
        },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          planzone,
          request: setRequestStateToSuccess(),
          defaultCalendarHolidays,
        },
      ];
}

function handleRetrieveDefaultCalendarHolidaysFailure(
  state: State,
  planzone: Boundaries["retrieveDefaultCalendarHolidays"]["failure"],
): State {
  const index = state.findIndex((req) => req.planzone.uuid === planzone.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], request: setRequestStateToFailure() },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          planzone,
          request: setRequestStateToFailure(),
          defaultCalendarHolidays: { ...calendarHolidays, uuid: "" },
        },
      ];
}

function handleAssignDefaultCalendarHolidaysRequest(
  state: State,
  {
    planzone,
    defaultCalendarHolidays,
  }: Boundaries["assignDefaultCalendarHolidaysToPlanzone"]["request"],
): State {
  const index = state.findIndex((req) => req.planzone.uuid === planzone.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          ...state[index],
          defaultCalendarHolidays: {
            ...defaultCalendarHolidays,
            uuid: state[index].defaultCalendarHolidays.uuid,
          },
        },
        ...state.slice(index + 1),
      ]
    : state;
}

function handleAssignDefaultCalendarHolidaysSuccess(
  state: State,
  {
    planzone,
    defaultCalendarHolidays,
  }: Boundaries["assignDefaultCalendarHolidaysToPlanzone"]["success"],
): State {
  const index = state.findIndex((req) => req.planzone.uuid === planzone.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          ...state[index],
          defaultCalendarHolidays: {
            ...defaultCalendarHolidays,
            uuid: state[index].defaultCalendarHolidays.uuid,
          },
        },
        ...state.slice(index + 1),
      ]
    : state;
}

function handleAssignDefaultCalendarHolidaysFailure(
  state: State,
  {
    planzone,
    defaultCalendarHolidays,
  }: Boundaries["assignDefaultCalendarHolidaysToPlanzone"]["failure"],
): State {
  const index = state.findIndex((req) => req.planzone.uuid === planzone.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], defaultCalendarHolidays },
        ...state.slice(index + 1),
      ]
    : state;
}

const reduceState = createStateReducer(initialState, {
  [events.retrieveDefaultCalendarHolidaysRequested]:
    handleRetrieveDefaultCalendarHolidaysRequest,
  [events.retrieveDefaultCalendarHolidaysSucceeded]:
    handleRetrieveDefaultCalendarHolidaysSuccess,
  [events.retrieveDefaultCalendarHolidaysFailed]:
    handleRetrieveDefaultCalendarHolidaysFailure,
  [events.assignDefaultCalendarHolidaysToPlanzoneRequested]:
    handleAssignDefaultCalendarHolidaysRequest,
  [events.assignDefaultCalendarHolidaysToPlanzoneSucceeded]:
    handleAssignDefaultCalendarHolidaysSuccess,
  [events.assignDefaultCalendarHolidaysToPlanzoneFailed]:
    handleAssignDefaultCalendarHolidaysFailure,
});

export type { State, Selector };

export { reduceState, initialState, createSelector };
